import { Platform } from 'react-native';

/**
 * Module-level Esc handler stack. The topmost open overlay handles
 * Escape; lower-stack handlers stay dormant until their turn at the
 * top. Solves the "single Esc cascades through every open overlay"
 * bug — when AdaptivePanel A is open and ConfirmDialog B is opened
 * over it, only B's handler fires on Escape.
 *
 * The previous per-component pattern used `document.addEventListener
 * + e.stopPropagation()`, which doesn't stop document-sibling listeners
 * (only stopImmediatePropagation does, and even that depends on
 * registration order — which is unstable when overlays mount in
 * arbitrary order).
 *
 * Used by `useEscapeToClose` (AdaptivePanel), AppToastViewport, and
 * SpeedDial. Web-only — native modals handle their own back-button
 * dismissal.
 */

type Handler = () => void;

const stack: Handler[] = [];
let listenerAttached = false;

function onKeyDown(e: KeyboardEvent) {
  if (e.key !== 'Escape') return;
  const top = stack[stack.length - 1];
  if (!top) return;
  e.preventDefault();
  e.stopPropagation();
  top();
}

function ensureListener() {
  if (listenerAttached || typeof document === 'undefined') return;
  document.addEventListener('keydown', onKeyDown);
  listenerAttached = true;
}

function teardownListener() {
  if (!listenerAttached || typeof document === 'undefined') return;
  document.removeEventListener('keydown', onKeyDown);
  listenerAttached = false;
}

/**
 * Push an Esc handler onto the stack while open. Returns a teardown
 * function — call on unmount / close to pop. Native is a no-op (the
 * RN Modal forwards Esc via onRequestClose, sheets handle their own).
 */
export function pushEscapeHandler(handler: Handler): () => void {
  if (Platform.OS !== 'web') return () => undefined;
  stack.push(handler);
  ensureListener();
  return () => {
    const idx = stack.lastIndexOf(handler);
    if (idx >= 0) stack.splice(idx, 1);
    if (stack.length === 0) teardownListener();
  };
}

/** Test-only — clear the stack between tests. */
export function _resetEscapeStack() {
  stack.length = 0;
  teardownListener();
}
